import { StateCreator } from "zustand";
import { NavigateFunction } from "react-router";
import { InboxAPI } from "api/inbox";
import { MessageItem, Thread, SentMessages } from "models/inbox/inbox";
import { Attachment } from "models/dashboard/dashboard";

import { RootState } from "store";

export const MessageType = {
  Normal: "normal",
  PeriodicReport: "report",
};

type DeleteMessageThreadPayload = {
  customerId: string;
  messageId: string;
  threadId: string;
  messageType: string;
  navigate: NavigateFunction;
};

type ThreadsResponse = {
  items: Thread[];
  totalCount: number;
};

export interface InboxSlice {
  isLoadingThread: boolean;
  isLoadingThreads: boolean;
  isLoadingAllThreads: boolean;
  totalThreads: number;
  myTotalThreads: number;
  allThreads: Thread[];
  myThreads: Thread[];
  sentMessages: MessageItem[];
  isLoadingPnlData: boolean;
  pnlData: {
    content: string;
    isLoaded: boolean;
  };
  currentThread?: Thread;
  loadingThreads: () => void;
  loadingAllThreads: () => void;
  appendingThreads: () => void;
  appendingThreadsAll: () => void;
  appendMyThreads: (threads: Thread[], totalThreads: number) => void;
  appendAllThreads: (threads: Thread[], totalThreads: number) => void;
  failedToLoadThreads: () => void;
  loadingThread: () => void;
  getThread: (thread: Thread) => void;
  failedToGetThread: () => void;
  getSentMessages: (messages: Omit<MessageItem, "subscribers">[]) => void;
  resetInbox: () => void;
  resetThreads: () => void;
  deleteThreadMessageFailed: (errorMessage: string) => void;
  loadInbox: () => Promise<void>;
  deleteThreadMessage: ({
    customerId,
    messageId,
    threadId,
    messageType,
    navigate,
  }: DeleteMessageThreadPayload) => void;
  fetchThread: (customerId: string, threadId: string) => void;
  fetchThreads: (
    customerId: string,
    getAllThreads?: boolean,
    skip?: number,
    searchTerms?: string,
  ) => void;
  fetchMoreThreads: (
    customerId: string,
    getAllThreads: boolean,
    skip: number,
    searchTerms?: string,
  ) => void;
  setThreadStatus: (threadId: string, action: string) => void;
  fetchSentMessages: (customerId: string) => void;
  sendMessage: (
    fromUserId: string,
    body: string,
    title: string,
    threadId: string | null,
    subscribers: string[],
    customerId: string,
    attachments: Attachment[],
    type?: string,
    reportStart?: string,
    reportEnd?: string,
  ) => Promise<void>;
}

export const createInboxSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  InboxSlice
> = (set, get) => ({
  isLoadingThread: false,
  isLoadingThreads: false,
  isLoadingAllThreads: false,
  totalThreads: 0,
  myTotalThreads: 0,
  allThreads: [],
  myThreads: [],
  currentThread: undefined,
  sentMessages: [],
  isLoadingPnlData: false,
  pnlData: {
    content: "",
    isLoaded: false,
  },
  loadingThreads: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          isLoadingThreads: true,
        },
      }),
      false,
      "inbox/LOADING_THREADS",
    ),
  loadingAllThreads: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          isLoadingAllThreads: true,
        },
      }),
      false,
      "inbox/LOADING_THREADS_ALL",
    ),
  appendingThreads: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          isLoadingThreads: true,
        },
      }),
      false,
      "inbox/APPENDING_THREADS",
    ),
  appendingThreadsAll: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          isLoadingAllThreads: true,
        },
      }),
      false,
      "inbox/LOADING_APPENDING_THREADS_ALL",
    ),
  appendMyThreads: (threads = [], total) =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          myThreads: [...inbox.myThreads, ...threads],
          myTotalThreads: total !== undefined ? total : inbox.myTotalThreads,
          isLoadingThreads: false,
        },
      }),
      false,
      "inbox/APPEND_THREADS",
    ),
  appendAllThreads: (threads = [], total) =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          allThreads: [...inbox.allThreads, ...threads],
          totalThreads: total !== undefined ? total : inbox.totalThreads,
          isLoadingAllThreads: false,
        },
      }),
      false,
      "inbox/APPEND_THREADS_ALL",
    ),
  failedToLoadThreads: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          isLoadingThreads: false,
          totalThreads: 0,
        },
      }),
      false,
      "inbox/LOADING_THREADS_FAILED",
    ),
  loadingThread: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          isLoadingThread: true,
          currentThread: undefined,
        },
      }),
      false,
      "inbox/GETTING_THREAD",
    ),
  getThread: (thread: Thread) =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          currentThread: thread,
          allThreads: inbox.allThreads.map((t) => {
            if (t.threadId === thread.threadId) {
              thread.lastMessage.isRead = true;
              thread.unread = false;
            }
            return thread;
          }),
          myThreads: inbox.myThreads.map((t) => {
            if (t.threadId === thread.threadId) {
              thread.lastMessage.isRead = true;
              thread.unread = false;
            }
            return thread;
          }),
          isLoadingThread: false,
        },
      }),
      false,
      "inbox/GET_THREAD",
    ),
  failedToGetThread: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          isLoadingThread: false,
        },
      }),
      false,
      "inbox/GET_THREAD_FAILED",
    ),
  getSentMessages: (messages: Omit<MessageItem, "subscribers">[]) =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          sentMessages: messages,
        },
      }),
      false,
      "inbox/GET_SENT_MESSAGES",
    ),
  resetInbox: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          totalThreads: 0,
          myThreads: [],
          allThreads: [],
          sentMessages: [],
          currentThread: undefined,
        },
      }),
      false,
      "inbox/INBOX_RESET",
    ),
  resetThreads: () =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          totalThreads: 0,
          myThreads: [],
          allThreads: [],
          currentThread: undefined,
        },
      }),
      false,
      "inbox/THREADS_RESET",
    ),
  deleteThreadMessageFailed: (errorMessage: string) =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
        },
      }),
      false,
      "inbox/DELETE_THREAD_MESSAGE_FAILED",
    ),
  loadInbox: async () => {
    const workingCustomerId = get().global.workingCustomerId;
    get().inbox.resetInbox();

    try {
      await Promise.all([
        get().inbox.fetchSentMessages(workingCustomerId),
        get().inbox.fetchThreads(workingCustomerId, true),
        get().inbox.fetchThreads(workingCustomerId),
        get().archive.fetchArchiveFolders(workingCustomerId),
      ]);
    } catch (error) {
      get().error.appendErrorNotification("Hämtning av inkorgen misslyckades.", error);
    }
  },
  deleteThreadMessage: async ({
    customerId,
    messageId,
    threadId,
    messageType = MessageType.Normal,
    navigate,
  }: DeleteMessageThreadPayload) => {
    const thread = get().inbox.currentThread;
    const threadDeleted = thread?.messages.length === 1;
    get().inbox.loadingThread();

    try {
      const result = await InboxAPI.deleteThreadMessage({
        customerId,
        messageId,
        threadId,
        messageType,
      });

      if (!result.ok || result.status !== 200) {
        throw new Error("Borttagning av konversationen misslyckades.");
      }

      if (result.ok || result.status === 200) {
        if (threadDeleted) {
          navigate("/inbox");
        }
      }

      get().inbox.loadingThreads();

      setTimeout(async () => {
        await get().inbox.loadInbox();
        if (!threadDeleted) {
          get().inbox.fetchThread(customerId, threadId);
        }
      }, 1000);

      return;
    } catch (error) {
      get().inbox.deleteThreadMessageFailed(
        `Borttagning av meddelande ${messageId} från tråd ${threadId} misslyckades.`,
      );
      get().error.appendErrorNotification(
        `Borttagning av meddelande ${messageId} från tråd ${threadId} misslyckades.`,
        error,
      );
    }
  },
  fetchThread: async (customerId: string, threadId: string) => {
    get().inbox.loadingThread();
    try {
      const result = await InboxAPI.fetchThread(customerId, threadId);

      get().status.setUnreadThreadsStatus(customerId)
      get().inbox.getThread(result);
    } catch (error) {
      get().inbox.failedToGetThread();
      get().error.appendErrorNotification(`Hämtning av konversationen misslyckades ${threadId}`, error);
    }
  },
  fetchThreads: async (
    customerId: string,
    getAllThreads = false,
    skip = 0,
    searchTerms = "",
  ) => {
    if (getAllThreads) {
      get().inbox.loadingAllThreads();
    } else {
      get().inbox.loadingThreads();
    }

    try {
      const result = await InboxAPI.fetchThreads(
        customerId,
        getAllThreads,
        skip,
        searchTerms,
      );
      const model: ThreadsResponse = result;
      if (getAllThreads) {
        get().inbox.appendAllThreads(model.items, model.totalCount);
      } else {
        get().inbox.appendMyThreads(model.items, model.totalCount);
      }
    } catch (error) {
      get().inbox.failedToLoadThreads();
      get().error.appendErrorNotification("Hämtning av konversationer misslyckades.", error);
    }
  },
  fetchMoreThreads: async (
    customerId: string,
    getAllThreads: boolean,
    skip: number,
    searchTerms?: string,
  ) => {
    if (getAllThreads) {
      get().inbox.appendingThreadsAll();
    } else {
      get().inbox.appendingThreads();
    }

    try {
      const response = await InboxAPI.fetchMoreThreads(
        customerId,
        getAllThreads,
        skip,
        searchTerms,
      );

      const model: ThreadsResponse = response;

      if (getAllThreads) {
        get().inbox.appendAllThreads(model.items, model.totalCount);
      } else {
        get().inbox.appendMyThreads(model.items, model.totalCount);
      }
    } catch (error) {
      get().inbox.failedToLoadThreads();
      get().error.appendErrorNotification(
        "Ytterligare konversationer kunde inte laddas.",
        error,
      );
    }
  },
  setThreadStatus: async (threadId: string, action: string) => {
    await InboxAPI.setThreadStatus(threadId, action);
    get().inbox.loadingThreads();
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await get().inbox.loadInbox();
  },
  fetchSentMessages: async (customerId: string) => {
    try {
      const result = await InboxAPI.fetchSentMessages(customerId);
      const model: SentMessages = result;
      get().inbox.getSentMessages(model.messages);
    } catch (error) {
      get().error.appendErrorNotification("Hämtning av meddelanden misslyckades.", error);
    }
  },
  sendMessage: async (
    fromUserId: string,
    body: string,
    title: string,
    threadId: string | null,
    subscribers: string[],
    customerId: string,
    attachments: Attachment[],
    type = MessageType.Normal,
    reportStart = "",
    reportEnd = "",
  ) => {
    try {
      const result = await InboxAPI.sendMessage(
        fromUserId,
        body,
        title,
        threadId,
        subscribers,
        customerId,
        attachments,
        type,
        reportStart,
        reportEnd,
      );

      const res = result;
      get().inbox.loadingThread();
      await get().inbox.loadInbox();
      get().inbox.fetchThread(customerId, res.threadId);
      return result;
    } catch (error) {
      get().error.appendErrorNotification("Skickning av meddelandet misslyckades.", error);
      return Promise.reject();
    }
  },
});
